<script setup lang="ts">
import { ref, watch } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import { t } from "../i18n";
const props = defineProps<{ open: boolean; busy?: boolean }>();
const emit = defineEmits<{
  close: [];
  confirm: [payload: { until: Date; reason?: string }];
}>();
const preset = ref("1h");
const custom = ref("");
const reason = ref("");
const presets = ["1h", "3h", "tomorrow", "next_week", "custom"] as const;
watch(
  () => props.open,
  (v) => {
    if (v) {
      preset.value = "1h";
      custom.value = "";
      reason.value = "";
    }
  },
);
function until(): Date | null {
  const now = new Date();
  switch (preset.value) {
    case "1h":
      return new Date(now.getTime() + 3600_000);
    case "3h":
      return new Date(now.getTime() + 3 * 3600_000);
    case "tomorrow": {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    }
    case "next_week": {
      const d = new Date(now);
      d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
      d.setHours(9, 0, 0, 0);
      return d;
    }
    default:
      return custom.value ? new Date(custom.value) : null;
  }
}
function confirm() {
  const u = until();
  if (!u || u.getTime() <= Date.now()) return;
  emit("confirm", {
    until: u,
    ...(reason.value.trim() ? { reason: reason.value.trim() } : {}),
  });
}
</script>
<template>
  <Modal :open="open" :title="t('snooze.title')" @close="emit('close')">
    <div class="modal-form">
      <p class="muted small">{{ t("snooze.hint") }}</p>
      <div class="choice-grid" role="radiogroup">
        <button
          v-for="p in presets"
          :key="p"
          class="chip"
          role="radio"
          :aria-checked="preset === p"
          :class="{ active: preset === p }"
          @click="preset = p"
        >
          {{ t(("snooze." + p) as never) }}
        </button>
      </div>
      <label v-if="preset === 'custom'" class="wa-field"
        ><span>{{ t("snooze.custom") }}</span
        ><input v-model="custom" type="datetime-local"
      /></label>
      <label class="wa-field"
        ><span>{{ t("snooze.reason") }}</span
        ><input v-model="reason" maxlength="200"
      /></label>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        :loading="busy"
        :disabled="preset === 'custom' && !custom"
        @click="confirm"
        >{{ t("snooze.confirm") }}</Button
      >
    </template>
  </Modal>
</template>
